import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Box,
  Button,
  Collapse,
  Typography,
  CircularProgress,
  TextField,
  Grid,
  Alert,
  Card,
  CardContent,
  Chip,
  Fade,
  Zoom,
  Tooltip,
  Container,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Apartment as ApartmentIcon,
  Person as PersonIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { getUsersByApartment } from "../../services/apartmentService";
import { useMovilidadElectrica } from "../../context/MovilidadElectricaContext";
import EditUserModal from "../User/EditUserModal";
import DeleteUsuarioModal from "./DeleteUsuarioModal";
import CreateApartmentUserModal from "./CreateApartmentUserModal";
import CreateApartmentModal from "./CreateApartmentModal";
import EditApartmentModal from "./EditApartmentModal";
import DeleteApartmentModal from "./DeleteApartmentModal";

const ApartmentCard = (props) => {
  const { row, onEditUserClick, onDeleteUserClick, onAddUserClick, onEditApartmentClick, onDeleteApartmentClick } = props;
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleToggle = async () => {
    if (!open && users.length === 0) {
      setLoading(true);
      setError(null);
      try {
        const fetchedUsers = await getUsersByApartment(row.apartment_id);
        setUsers(fetchedUsers);
      } catch (error) {
        console.error("Failed to fetch users for apartment", row.apartment_id, error);
        setError("No se pudieron cargar los usuarios.");
        setUsers([]);
      }
      setLoading(false);
    }
    setOpen(!open);
  };

  return (
    <Fade in={true} timeout={300}>
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 3,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 2,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: (theme) => theme.shadows[12]
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {/* Header del apartamento */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            justifyContent: 'space-between', 
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, flex: 1 }}>
              <Box 
                sx={{ 
                  p: { xs: 1.5, md: 2 }, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 3,
                  minWidth: { xs: 48, md: 56 },
                  minHeight: { xs: 48, md: 56 }
                }}
              >
                <HomeIcon sx={{ color: '#FFFFFF', fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#002642',
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                  }}
                >
                  Apartamento {row.matricula}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#75787B',
                    fontSize: { xs: '0.8rem', md: '0.875rem' }
                  }}
                >
                  ID: {row.apartment_id}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1, md: 2 },
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-end', sm: 'flex-start' }
            }}>
              <Tooltip title="Editar apartamento">
                <IconButton 
                  onClick={() => onEditApartmentClick(row)}
                  sx={{ 
                    color: '#002642',
                    backgroundColor: 'rgba(0, 38, 66, 0.1)',
                    '&:hover': { 
                      backgroundColor: '#002642',
                      color: '#FFFFFF',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar apartamento">
                <IconButton 
                  onClick={() => onDeleteApartmentClick(row)}
                  sx={{ 
                    color: '#F44336',
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    '&:hover': { 
                      backgroundColor: '#F44336',
                      color: '#FFFFFF',
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={open ? "Ocultar usuarios" : "Ver usuarios"}>
                <IconButton 
                  onClick={handleToggle}
                  sx={{ 
                    color: '#DB9600',
                    backgroundColor: 'rgba(219, 150, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    '&:hover': { 
                      backgroundColor: '#DB9600',
                      color: '#FFFFFF'
                    }
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Lista expandible de usuarios */}
          <Collapse in={open} timeout={400}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mt: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                mb: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    color: '#002642',
                    fontSize: { xs: '1rem', md: '1.25rem' }
                  }}
                >
                  <PersonIcon sx={{ color: '#DB9600', fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
                  Usuarios Registrados
                </Typography>
                <Button 
                  variant="contained" 
                  size="medium" 
                  startIcon={<AddIcon />}
                  onClick={() => onAddUserClick(row.apartment_id)}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: '#DB9600',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    px: { xs: 2, md: 3 },
                    minWidth: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: '#B87900',
                      color: '#FFFFFF'
                    }
                  }}
                >
                  Añadir Usuario
                </Button>
              </Box>
              
              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={32} />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
              ) : (
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {users.length > 0 ? (
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                      {users.map((user, index) => (
                        <Grid item xs={12} sm={6} lg={4} xl={3} key={user.id}>
                          <Zoom in={true} timeout={200 + index * 100}>
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                borderRadius: 2,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: 'secondary.main',
                                  boxShadow: 2
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#002642' }}>
                                    {user.username}
                                  </Typography>
                                  <Chip 
                                    label={`ID: ${user.user_id}`} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ 
                                      mt: 0.5,
                                      borderColor: '#DB9600',
                                      color: '#002642',
                                      backgroundColor: 'rgba(219, 150, 0, 0.1)'
                                    }}
                                  />
                                </Box>
                                <Box>
                                  <Tooltip title="Editar usuario">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => onEditUserClick(user)}
                                      sx={{ 
                                        color: '#002642', 
                                        mr: 0.5,
                                        backgroundColor: 'rgba(0, 38, 66, 0.1)',
                                        '&:hover': { 
                                          backgroundColor: '#002642',
                                          color: '#FFFFFF'
                                        }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Eliminar usuario">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => onDeleteUserClick(user)}
                                      sx={{ 
                                        color: '#F44336',
                                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                        '&:hover': { 
                                          backgroundColor: '#F44336',
                                          color: '#FFFFFF'
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Card>
                          </Zoom>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No hay usuarios asignados a este apartamento.
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Fade>
  );
};

const UsuariosRegistradosList = () => {
  const { apartments, refreshApartments, loading, error } = useMovilidadElectrica();
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [isCreateApartmentUserModalOpen, setIsCreateApartmentUserModalOpen] = useState(false);
  const [isCreateApartmentModalOpen, setIsCreateApartmentModalOpen] = useState(false);
  const [isEditApartmentModalOpen, setIsEditApartmentModalOpen] = useState(false);
  const [isDeleteApartmentModalOpen, setIsDeleteApartmentModalOpen] = useState(false);
  const [selectedApartmentId, setSelectedApartmentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    refreshApartments();
  }, [refreshApartments]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0);
  };

  const handleAddUserClick = (apartmentId) => {
    setSelectedApartmentId(apartmentId);
    setIsCreateApartmentUserModalOpen(true);
  };

  const handleEditUserClick = (user) => {
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleDeleteUserClick = (userAssociation) => {
    setSelectedUser(userAssociation);
    setIsDeleteUserModalOpen(true);
  };

  const handleEditApartmentClick = (apartment) => {
    setSelectedApartment(apartment);
    setIsEditApartmentModalOpen(true);
  };

  const handleDeleteApartmentClick = (apartment) => {
    setSelectedApartment(apartment);
    setIsDeleteApartmentModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsEditUserModalOpen(false);
    setIsDeleteUserModalOpen(false);
    setIsCreateApartmentUserModalOpen(false);
    setIsCreateApartmentModalOpen(false);
    setIsEditApartmentModalOpen(false);
    setIsDeleteApartmentModalOpen(false);
    setSelectedUser(null);
    setSelectedApartment(null);
  };

  const handleApartmentCreated = () => {
    handleCloseModals();
    refreshApartments();
  };

  const handleApartmentUpdated = () => {
    handleCloseModals();
    refreshApartments();
  };

  const handleApartmentDeleted = () => {
    handleCloseModals();
    refreshApartments();
  };

  const handleUserUpdated = () => {
    handleCloseModals();
    refreshApartments();
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const filteredApartments = apartments.data.filter((apartment) =>
    apartment.matricula.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedApartments = filteredApartments.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, md: 3, lg: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        minHeight: 'calc(100vh - 64px)', // Ajustar altura considerando el navbar
        backgroundColor: '#FAFAFA'
      }}
    >
      {/* Header moderno con gradiente */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
          borderRadius: { xs: 2, md: 4 },
          p: { xs: 3, sm: 4, md: 4, lg: 5 },
          mb: { xs: 3, md: 4 },
          color: '#FFFFFF',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: 4
        }}
      >
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            right: 0,
            width: 100,
            height: 100,
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            transform: 'translate(50%, -50%)'
          }}
        />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 0 },
          position: 'relative' 
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                mb: 1, 
                color: '#FFFFFF',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Gestión de Usuarios
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                fontSize: { xs: '1rem', md: '1.25rem' },
                flexWrap: { xs: 'wrap', sm: 'nowrap' }
              }}
            >
              <ApartmentIcon sx={{ color: '#FFFFFF' }} />
              Administra apartamentos y usuarios del sistema
            </Typography>
          </Box>
          <Button 
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateApartmentModalOpen(true)}
            sx={{ 
              backgroundColor: '#DB9600',
              color: '#FFFFFF',
              fontWeight: 700,
              px: { xs: 3, md: 4 },
              py: 2,
              borderRadius: 3,
              boxShadow: 3,
              border: '2px solid #DB9600',
              minWidth: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#B87900',
                borderColor: '#B87900',
                color: '#FFFFFF',
                transform: 'translateY(-2px)',
                boxShadow: 6
              }
            }}
          >
            Crear Apartamento
          </Button>
        </Box>
      </Box>

      {/* Barra de búsqueda mejorada */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
            <Grid item xs={12} lg={8}>
              <TextField
                variant="outlined"
                placeholder="Buscar por matrícula del apartamento..."
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#75787B' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    fontSize: { xs: '0.9rem', md: '1rem' }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                flexWrap: 'wrap'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#75787B', 
                    fontWeight: 500,
                    fontSize: { xs: '0.8rem', md: '0.875rem' }
                  }}
                >
                  {filteredApartments.length} apartamento{filteredApartments.length !== 1 ? 's' : ''} encontrado{filteredApartments.length !== 1 ? 's' : ''}
                </Typography>
                <Chip 
                  icon={<ApartmentIcon sx={{ color: '#FFFFFF', fontSize: '1rem' }} />}
                  label={`Total: ${apartments.data.length}`}
                  sx={{
                    backgroundColor: '#002642',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    border: '1px solid #002642',
                    fontSize: { xs: '0.75rem', md: '0.8125rem' }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lista de apartamentos como cards */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : error ? (
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: 3, 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '1.1rem'
            }
          }}
        >
          Error al cargar los apartamentos: {error.message}
        </Alert>
      ) : filteredApartments.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: 'center', p: 6 }}>
          <ApartmentIcon sx={{ fontSize: 64, color: '#75787B', mb: 2 }} />
          <Typography variant="h5" sx={{ color: '#002642', mb: 2, fontWeight: 600 }}>
            No se encontraron apartamentos
          </Typography>
          <Typography variant="body1" sx={{ color: '#75787B', mb: 3 }}>
            {searchTerm ? 'Prueba con otro término de búsqueda' : 'Comienza creando tu primer apartamento'}
          </Typography>
          <Button 
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateApartmentModalOpen(true)}
            sx={{
              backgroundColor: '#002642',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#00577A'
              }
            }}
          >
            Crear Apartamento
          </Button>
        </Card>
      ) : (
        <>
          {paginatedApartments.map((apartment, index) => (
            <ApartmentCard 
              key={apartment.apartment_id} 
              row={apartment} 
              onEditUserClick={handleEditUserClick} 
              onDeleteUserClick={handleDeleteUserClick} 
              onAddUserClick={handleAddUserClick}
              onEditApartmentClick={handleEditApartmentClick}
              onDeleteApartmentClick={handleDeleteApartmentClick}
            />
          ))}
          
          {/* Paginación mejorada */}
          {filteredApartments.length > rowsPerPage && (
            <Card sx={{ mt: 3, borderRadius: 3 }}>
              <TablePagination
                component="div"
                count={filteredApartments.length}
                page={currentPage}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 15, 25]}
                labelRowsPerPage="Apartamentos por página:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{ 
                  '& .MuiTablePagination-toolbar': {
                    px: 3,
                    py: 2
                  }
                }}
              />
            </Card>
          )}
        </>
      )}

      {/* Modales */}
      <CreateApartmentModal isOpen={isCreateApartmentModalOpen} onClose={handleCloseModals} onApartmentCreated={handleApartmentCreated} />
      <EditApartmentModal isOpen={isEditApartmentModalOpen} onClose={handleCloseModals} apartment={selectedApartment} onApartmentUpdated={handleApartmentUpdated} />
      <DeleteApartmentModal isOpen={isDeleteApartmentModalOpen} onClose={handleCloseModals} apartment={selectedApartment} onApartmentDeleted={handleApartmentDeleted} />

      {isCreateApartmentUserModalOpen && (
        <CreateApartmentUserModal
          isOpen={isCreateApartmentUserModalOpen}
          onClose={() => setIsCreateApartmentUserModalOpen(false)}
          apartmentId={selectedApartmentId}
        />
      )}
      <EditUserModal
        isOpen={isEditUserModalOpen}
       onClose={handleCloseModals}
       user={selectedUser}
       onSave={handleUserUpdated}
     />
     <DeleteUsuarioModal
       isOpen={isDeleteUserModalOpen}
        onClose={handleCloseModals}
        userAssociation={selectedUser} // Pasamos el objeto de asociación completo
        onUserDeleted={() => {
          handleCloseModals();
          // Forzamos un refresh de la lista de apartamentos para que se actualice el sub-componente
          refreshApartments();
        }}
      />
    </Container>
  );
};

export default UsuariosRegistradosList;
